import { useCallback } from 'react'
import { Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTableFacetedFilter } from '@/views/users/components/data-table-faceted-filter'
import { DataTableViewOptions } from '@/components/data-table/data-table-view-options'
import { PaginatedSelect } from '@/components/paginated-select'
import { get_clients } from '@/api/client'
import type { InvoicesTableParams } from '../index'
import type { InvoiceStatus } from '@/api/invoicing'

const STATUS_OPTIONS: { labelKey: string; value: InvoiceStatus }[] = [
  { labelKey: 'inv_status_draft',     value: 'draft' },
  { labelKey: 'inv_status_sent',      value: 'sent' },
  { labelKey: 'inv_status_partial',   value: 'partial' },
  { labelKey: 'inv_status_paid',      value: 'paid' },
  { labelKey: 'inv_status_cancelled', value: 'cancelled' },
]

interface InvoicesToolbarProps<TData> {
  table: Table<TData>
  params: InvoicesTableParams
  setParams: React.Dispatch<React.SetStateAction<InvoicesTableParams>>
}

export function InvoicesToolbar<TData>({ table, params, setParams }: InvoicesToolbarProps<TData>) {
  const { t } = useTranslation()

  const hasFilters = !!params.status || !!params.client

  const statusOptions = STATUS_OPTIONS.map((o) => ({ label: t(o.labelKey), value: o.value }))

  const fetchClientItems = useCallback(async (page: number, search?: string) => {
    const result = await get_clients({ page, page_size: 20, name: search || null, ordering: null })
    return {
      items: result.results.map((c) => ({ id: c.id, name: c.name })),
      hasMore: !!result.next,
    }
  }, [])

  const selectedClientValue =
    params.client && params.client_name
      ? { id: params.client, name: params.client_name }
      : null

  const handleStatusChange = (values: string[]) => {
    setParams((p) => ({
      ...p,
      status: values.length > 0 ? (values[0] as InvoiceStatus) : undefined,
      page: 0,
    }))
  }

  const handleClientChange = (item: { id: string | number; name: string } | null) => {
    setParams((p) => ({
      ...p,
      client: item ? String(item.id) : undefined,
      client_name: item?.name,
      page: 0,
    }))
  }

  const resetAll = () => {
    setParams((p) => ({ ...p, status: undefined, client: undefined, client_name: undefined, page: 0 }))
  }

  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <DataTableFacetedFilter
          title={t('inv_col_status')}
          options={statusOptions}
          defaultSelected={params.status ? [params.status] : []}
          onFacetChange={handleStatusChange}
        />

        <PaginatedSelect
          placeholder={t('inv_col_client')}
          fetchItems={fetchClientItems}
          value={selectedClientValue}
          onSelect={handleClientChange}
          className="w-[200px]"
        />

        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetAll}
            className="h-9 px-2 text-muted-foreground hover:text-foreground"
          >
            {t('reset_filters')}
            <X className="ml-1 h-4 w-4" />
          </Button>
        )}
      </div>

      <DataTableViewOptions table={table} />
    </div>
  )
}
